import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import {
  enforceMutualExclusivity,
  MUTUALLY_EXCLUSIVE_OPTION_GROUPS,
} from '@/pages/PortfolioAnalysis/PortfolioGraphing/utils';
import CheckList from '../../../CheckList';

const useStyles = makeStyles(theme => ({
  checkListRoot: {
    padding: 0,
  },
  label: {
    ...theme.palette.alternateText,
    fontWeight: 'bold',
  },
  border: {
    borderBottom: 'solid thin rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  borderOptions: {
    flexDirection: 'row',
  },
  radioRow: {
    width: '100%',
    justifyContent: 'space-between',
    margin: 0,
  },
  radioRowLabel: {
    fontWeight: 'bold',
  },
  radioRowOption: {
    width: 'auto',
    justifyContent: 'unset',
  },
  radioRowOptionLabel: {
    width: 'fit-content',
    paddingTop: '10px',
  },
}));

export default ({
  graphTypes,
  graphTypeChildren = {},
  handleSetState,
  includeOptions,
  itemIsDisabledParams = {},
  selectedAggregate,
  selectedGraphType,
  selectedGraphTypeOptions,
  label = 'Select Metric',
}) => {
  const classes = useStyles();

  const onChange = e => {
    // Find the selected graph type object from the value string
    let foundGraphType;
    // Loop through graph types
    for (let i = 0; i < graphTypes.length; i++) {
      const gt = graphTypes[i];

      // If graph type contains options, search value from options
      if (gt.options) {
        // Loop through options
        for (let j = 0; j < gt.options.length; j++) {
          const op = gt.options[j];
          if (op.value === e.target.value) {
            foundGraphType = op;
            // If graph type was found break out of loop
            break;
          }
        }
      } else {
        // If graph type does not contain options, search value from graph type
        if (gt.value === e.target.value || gt === e.target.value) {
          foundGraphType = gt;
        }
      }
      // If graph type was found break out of loop
      if (foundGraphType) break;
    }

    // If selected graph type is an object, set value to object, otherwise set value to string
    const value = typeof selectedGraphType === 'object' ? foundGraphType : foundGraphType?.value;
    // Set state with found graph type
    handleSetState(value, 'graphType');
  };

  const setGraphTypeOptions = useCallback(
    (options = []) => handleSetState(options, 'graphTypeOptions'),
    [handleSetState],
  );

  // Simple callback - just update state directly
  const handleGraphTypeOptionsChange = useCallback(
    (options = []) => {
      handleSetState(Array.isArray(options) ? options : [], 'graphTypeOptions');
    },
    [handleSetState],
  );

  // Separate effect to enforce mutual exclusivity - only fixes conflicts, doesn't cause loops
  const previousOptionsRef = useRef(selectedGraphTypeOptions);
  const lastSetOptionsRef = useRef(null); // Track what we just set to prevent loops

  useEffect(() => {
    const current = selectedGraphTypeOptions || [];
    const previous = previousOptionsRef.current || [];

    // Skip if this is the value we just set (prevents infinite loop)
    const currentSignature = current
      .map(opt => opt.name)
      .sort()
      .join(',');
    if (lastSetOptionsRef.current === currentSignature) {
      // This is the update we triggered, update ref and exit
      previousOptionsRef.current = current;
      lastSetOptionsRef.current = null; // Clear the flag
      return;
    }

    // Check if there are any mutual exclusivity conflicts
    const hasConflict = MUTUALLY_EXCLUSIVE_OPTION_GROUPS.some(group => {
      const optionsInGroup = current.filter(option => group.includes(option.name));
      return optionsInGroup.length > 1;
    });

    // Only process if there's a conflict AND it's different from previous
    if (hasConflict) {
      const filteredOptions = enforceMutualExclusivity(current, previous);
      const currentNames = current
        .map(opt => opt.name)
        .sort()
        .join(',');
      const filteredNames = filteredOptions
        .map(opt => opt.name)
        .sort()
        .join(',');

      // Only update if filtering actually changed something
      if (currentNames !== filteredNames) {
        const filteredSignature = filteredOptions
          .map(opt => opt.name)
          .sort()
          .join(',');
        lastSetOptionsRef.current = filteredSignature; // Track what we're about to set
        previousOptionsRef.current = filteredOptions;
        handleSetState(filteredOptions, 'graphTypeOptions');
        return; // Exit early
      }
    }

    // Update ref for next comparison (only if we didn't update state)
    previousOptionsRef.current = current;
  }, [selectedGraphTypeOptions, handleSetState]);
  // Reset graph type options when aggregate or graph type changes
  const { resetGraphTypeOptions } = selectedGraphType;
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding selectedGraphTypeOptions causes "Maximum update depth exceeded" error --- IGNORE ---
  useEffect(() => {
    // A graph type with options is selected
    // Filter out options that aren't compatible with the aggregate
    if (resetGraphTypeOptions) {
      setGraphTypeOptions(
        resetGraphTypeOptions({
          aggregate: selectedAggregate,
          graphTypeOptions: selectedGraphTypeOptions,
        }),
      );
    }
  }, [selectedAggregate]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: adding selectedGraphTypeOptions causes "Maximum update depth exceeded" error --- IGNORE ---
  useEffect(() => {
    const { resetGraphTypeOptions } = selectedGraphType;
    // A graph type with options has been selected
    // Filter out options that don't match that graph type parent
    if (resetGraphTypeOptions) {
      setGraphTypeOptions(
        resetGraphTypeOptions({
          aggregate: selectedAggregate,
          graphType: selectedGraphType,
          graphTypeOptions: selectedGraphTypeOptions,
        }),
      );
    } else {
      // A graph type with no options has been selected
      // Reset graph type options to empty array
      setGraphTypeOptions();
    }
    if (selectedGraphType.defaultSort) {
      handleSetState(selectedGraphType.defaultSort, 'sortMetric');
    }
  }, [selectedGraphType]);

  return (
    <FormControl style={{ width: '100%' }}>
      <Typography className={classes.label} variant='overline'>
        {label}
      </Typography>
      <RadioGroup
        value={typeof selectedGraphType === 'object' ? selectedGraphType.value : selectedGraphType}
        name='graph-metric-radio-buttons-group'
        onChange={onChange}
      >
        {graphTypes.map(type => {
          const selectedOption =
            type.options?.find(option => option.value === selectedGraphType?.value) ?? null;
          let childrenList = [];
          if (type.options) {
            // Collect unique children from all child options
            const allChildren = new Map();
            type.options.forEach(option => {
              const optionChildren = graphTypeChildren[option.value] || [];
              optionChildren.forEach(child => {
                if (!allChildren.has(child.name)) {
                  allChildren.set(child.name, child);
                }
              });
            });
            childrenList = Array.from(allChildren.values());
          } else {
            const childrenKey = type.value;
            childrenList = graphTypeChildren[childrenKey] || [];
          }

          const isTypeSelected = selectedOption ? true : type.value === selectedGraphType?.value;

          // Define isCheckedCallback - checks if option is selected when type is selected
          const isCheckedCallback = option =>
            isTypeSelected && !!selectedGraphTypeOptions.find(c => c.name === option.name);

          return (
            <div
              className={clsx({ [classes.border]: true, [classes.borderOptions]: !!type.options })}
              data-has-options={!!type.options}
              key={`portfolio-analysis-graph-metric-${type.label}`}
            >
              {type.options ? (
                <>
                  <Typography
                    className={clsx(classes.radioRowLabel, classes.radioRowOptionLabel)}
                    variant='body1'
                  >
                    {type.label}
                  </Typography>
                  <div className={classes.radioOptionsContainer}>
                    {type.options.map(option => (
                      <FormControlLabel
                        key={`portfolio-analysis-graph-metric-${type.label}-${option.optionLabel}`}
                        className={clsx(classes.radioRow, classes.radioRowOption)}
                        value={option.value}
                        control={<Radio />}
                        disabled={type.isDisabled?.(itemIsDisabledParams)}
                        label={option.optionLabel}
                        labelPlacement='start'
                      />
                    ))}
                  </div>
                </>
              ) : (
                <Tooltip title={type.tooltip || ''}>
                  <FormControlLabel
                    className={classes.radioRow}
                    classes={{ label: classes.radioRowLabel }}
                    value={type.value}
                    control={<Radio />}
                    disabled={type.isDisabled?.(itemIsDisabledParams)}
                    label={type.label}
                    labelPlacement='start'
                  />
                </Tooltip>
              )}
              {includeOptions && childrenList.length > 0 && (
                <CheckList
                  styles={classes}
                  callback={handleGraphTypeOptionsChange}
                  checked={selectedGraphTypeOptions || []}
                  isDisabled={type.optionIsDisabled({
                    aggregate: selectedAggregate,
                    graphType: selectedGraphType,
                  })}
                  labelPlacement='start'
                  list={childrenList}
                  isCheckedCallback={isCheckedCallback}
                />
              )}
            </div>
          );
        })}
      </RadioGroup>
    </FormControl>
  );
};
